/**
 * Props for fetching component response from CMS.
 *
 * @template T
 * @typedef {Object} ComponentResponseProps
 * @property {string} [deliveryId] - Unique identifier for the content delivery.
 * @property {string} [locale] - Language/locale for the content.
 * @property {string} [vse] - Optional VSE (Versioned State Engine) parameter.
 * @property {T} [content] - Optional pre-existing content.
 */

/**
 * Fetches component response from CMS, with caching and error handling.
 *
 * @template T
 * @async
 * @function useComponentResponse
 * @param {ComponentResponseProps<T>} props - Configuration for fetching component response.
 *
 * @returns {Promise<ComputedRef<T>>} A computed reference to the fetched content.
 *
 * @description
 * This function:
 * - Returns pre-existing content if provided
 * - Fetches content from CMS API if no content is present
 * - Uses caching mechanism to improve performance
 * - Throws an error if content retrieval fails
 *
 * @example
 * const data = await useComponentResponse({
 *   deliveryId: 'component123',
 *   locale: 'en-US',
 *   vse: 'preview'
 * })
 *
 * @throws {Error} If content retrieval fails
 */
export async function useComponentResponse<T>(props: {
  deliveryId?: string
  locale?: string
  vse?: string
  content?: T
}) {
  const content = computed(() => props.content)

  if (content?.value) {
    return content
  }
  const url = `/api/cms/getByKey?locale=${props.locale}&id=${props.deliveryId}&vse=${props.vse ?? ''}`
  const { data, error } = await useFetch<T>(url, {
    key: `componentResponse__${props.deliveryId}__${props.locale}__${props.vse}`,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  })
  if (error.value) {
    // createError is not available in the client
    throw createError(error.value)
  }
  return data
}
